import { Interval, ObjectKind, ReportKind } from "@digits-graphql/frontend/graphql-bearer"
import {
  createRoutes,
  DetailsViewAction,
  type RouteConfig,
  type SharedObjectInfo,
  StaticRoutes,
} from "@digits-shared/components/Router/DigitsRoute"
import { RouteDefaultIntervalOrigin } from "@digits-shared/helpers/dateTimeHelper"
import { isMobile } from "@digits-shared/helpers/devicesHelper"
import {
  LE_SLUG_PARAM,
  ORG_SLUG_PARAM,
  PLG_ONBOARD_KIND_OPERATOR,
  PLG_ONBOARD_KIND_PATH_PARAM,
} from "src/frontend/components/Onboard/PLG/Shared/routeParams"
import { ORG_DASHBOARD_MODULES } from "src/frontend/components/OS/Applications/OrgDashboard/modules"
import { USER_DASHBOARD_MODULES } from "src/frontend/components/OS/Applications/UserDashboard/modules"
import { TabNames } from "src/frontend/components/Shared/Layout/types"
import {
  FrontendPartyRole,
  SUPPORTED_PARTY_ROLES_URL_KEYS,
} from "src/frontend/types/FrontendPartyRole"
import {
  DIMENSION_KINDS,
  isDimensionKind,
  pluralizeDimension,
} from "src/shared/components/Dimensions/dimensionsTypes"

export class FrontendStaticRoutes extends StaticRoutes {
  root = "/"

  // Session
  login = "/login"
  logout = "/logout"
  link = "/link"
  ssoComplete = "/sso/complete"
  macBootstrap = "/mac/bootstrap"
  mfaChallenge: RouteConfig = {
    parameterizedPath: "/mfa-challenge",
    moduleName: "Verify Identity",
  }

  // Password
  passwordReset = "/password-reset"

  // Signup
  welcome = "/welcome"
  /**
   * @deprecated send to firm dashboard instead
   */
  onboardApplicationStatus = "/onboard/application-status"
  onboardQuickbooksCallback = "/onboard/link-ledger" // it wont render anything (needs to match QBO, dont change)
  plgOnboardingRoot: RouteConfig = {
    parameterizedPath: "/join",
    moduleName: "Join Digits",
  }
  plgOnboardingKindKnown: RouteConfig = {
    parentRoute: this.plgOnboardingRoot,
    parameterizedPath: `/:${PLG_ONBOARD_KIND_PATH_PARAM}`,
  }
  plgOnboardingEntityKnown: RouteConfig = {
    parentRoute: this.plgOnboardingKindKnown,
    parameterizedPath: `/@:${LE_SLUG_PARAM}`,
  }

  // Operator Reset (Start Over)
  operatorResetEntity: RouteConfig = {
    parentRoute: this.plgOnboardingRoot,
    parameterizedPath: `/${PLG_ONBOARD_KIND_OPERATOR}/@:${LE_SLUG_PARAM}/reset`,
  }
  operatorResetEntitySetup: RouteConfig = {
    parentRoute: this.operatorResetEntity,
    parameterizedPath: "/setup",
  }

  plgClientAcceptAccountantInvite: RouteConfig = {
    parentRoute: this.plgOnboardingRoot,
    parameterizedPath: "/client",
    moduleName: "Join Digits",
  }
  plgClientAcceptAccountantEntityKnown: RouteConfig = {
    parentRoute: this.plgClientAcceptAccountantInvite,
    parameterizedPath: `/@:${LE_SLUG_PARAM}`,
  }

  plgFirmAcceptInvite: RouteConfig = {
    parentRoute: this.plgOnboardingRoot,
    parameterizedPath: "/firm",
    moduleName: "Join Digits",
  }
  plgFirmAcceptInviteOrgKnown: RouteConfig = {
    parentRoute: this.plgFirmAcceptInvite,
    parameterizedPath: `/@:${ORG_SLUG_PARAM}`,
  }

  // OAuth
  oauthAuthorize: RouteConfig = {
    parameterizedPath: "/oauth/authorize",
    moduleName: "OAuth Authorization",
  }

  // Email Preferences
  emailPreferencesRoot: RouteConfig = {
    parameterizedPath: "/email",
    moduleName: "Email Preferences",
  }
  emailUnsubscribe: RouteConfig = {
    parentRoute: this.emailPreferencesRoot,
    parameterizedPath: "/unsubscribe",
    moduleName: "Unsubscribe",
  }

  // LEGACY Signup / Onboard
  legacySignup = "/signup"
  onboard = "/onboard"

  internalFeatures = {
    parameterizedPath: "/internal-features",
    moduleName: "Employee Features",
  }
  internalDGRedirect = {
    parameterizedPath: "/support/dg", // keeping old path because of go-services ReferrerLinkFormat
    moduleName: "Referrer Link",
  }

  // Firm Dashboard
  firmDashboardRoot: RouteConfig = {
    parameterizedPath: "/firm",
    moduleName: "All Clients",
  }
  firmDashboardFirmKnown: RouteConfig = {
    parentRoute: this.firmDashboardRoot,
    parameterizedPath: `/@:${ORG_SLUG_PARAM}`,
    moduleName: "All Clients",
  }
  firmDashboardGrid: RouteConfig = {
    parentRoute: this.firmDashboardFirmKnown,
    parameterizedPath: "/grid",
    moduleName: "All Clients",
  }
  firmDashboardList: RouteConfig = {
    parentRoute: this.firmDashboardFirmKnown,
    parameterizedPath: "/list",
    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.CurrentMonth,
    },
    moduleName: "All Clients",
  }
  firmDashboardNewClient: RouteConfig = {
    parentRoute: this.firmDashboardFirmKnown,
    parameterizedPath: "/new/client",
    moduleName: "Add Client",
  }
  // Org Dashboard
  orgDashboardRoot: RouteConfig = {
    parameterizedPath: "/organization",
    moduleName: "Dashboard",
  }
  orgDashboardOrgKnown: RouteConfig = {
    parentRoute: this.orgDashboardRoot,
    parameterizedPath: `/@:${ORG_SLUG_PARAM}`,
    moduleName: ORG_DASHBOARD_MODULES.allEntities,
  }
  orgDashboardTeam: RouteConfig = {
    parentRoute: this.orgDashboardOrgKnown,
    parameterizedPath: "/team",
    moduleName: ORG_DASHBOARD_MODULES.manageTeam,
  }
  orgDashboardTeamMember: RouteConfig = {
    parentRoute: this.orgDashboardTeam,
    parameterizedPath: "/:memberId",
    moduleName: ORG_DASHBOARD_MODULES.manageTeam,
  }
  orgDashboardEntities: RouteConfig = {
    parentRoute: this.orgDashboardOrgKnown,
    parameterizedPath: "/entities",
    moduleName: ORG_DASHBOARD_MODULES.manageEntities,
  }
  orgDashboardEntity: RouteConfig = {
    parentRoute: this.orgDashboardEntities,
    parameterizedPath: `/@:${LE_SLUG_PARAM}`,
    moduleName: ORG_DASHBOARD_MODULES.manageEntities,
  }
  orgDashboardNewEntity: RouteConfig = {
    parentRoute: this.orgDashboardOrgKnown,
    parameterizedPath: "/new/entity",
    moduleName: ORG_DASHBOARD_MODULES.addEntity,
  }
  orgDashboardSettings: RouteConfig = {
    parentRoute: this.orgDashboardOrgKnown,
    parameterizedPath: "/settings",
    moduleName: ORG_DASHBOARD_MODULES.settings,
  }
  orgDashboardSettingsProfile: RouteConfig = {
    parentRoute: this.orgDashboardSettings,
    parameterizedPath: "/profile",
    moduleName: ORG_DASHBOARD_MODULES.profile,
  }
  orgDashboardSettingsSubscription: RouteConfig = {
    parentRoute: this.orgDashboardSettings,
    parameterizedPath: "/subscription",
    moduleName: ORG_DASHBOARD_MODULES.subscription,
  }

  userDashboardRoot: RouteConfig = {
    parameterizedPath: "/me",
    moduleName: USER_DASHBOARD_MODULES.dashboard,
  }
  userDashboardNew: RouteConfig = {
    parentRoute: this.userDashboardRoot,
    parameterizedPath: "/new",
    moduleName: USER_DASHBOARD_MODULES.dashboard,
  }
  userDashboardNewBusiness: RouteConfig = {
    parentRoute: this.userDashboardNew,
    parameterizedPath: "/business",
    moduleName: USER_DASHBOARD_MODULES.dashboard,
  }
  userDashboardNewFirm: RouteConfig = {
    parentRoute: this.userDashboardNew,
    parameterizedPath: "/firm",
    moduleName: USER_DASHBOARD_MODULES.dashboard,
  }
  userDashboardNewDeveloper: RouteConfig = {
    parentRoute: this.userDashboardNew,
    parameterizedPath: "/developer",
    moduleName: USER_DASHBOARD_MODULES.dashboard,
  }

  firmSettings: RouteConfig = {
    parentRoute: this.firmDashboardFirmKnown,
    parameterizedPath: "/settings",
    moduleName: "Firm Settings",
    detailsViewAction: DetailsViewAction.Push,
  }
  firmSettingsEmployeesSettings: RouteConfig = {
    parentRoute: this.firmSettings,
    parameterizedPath: "/employees",
  }
  firmSettingsEmployeeSettings: RouteConfig = {
    parentRoute: this.firmSettingsEmployeesSettings,
    parameterizedPath: "/:employeeId",
  }
  firmSettingsClients: RouteConfig = {
    parentRoute: this.firmSettings,
    parameterizedPath: "/client",
  }
  firmSettingsClient: RouteConfig = {
    parentRoute: this.firmSettingsClients,
    parameterizedPath: "/@:leSlug",
  }
  firmSettingsSubscription: RouteConfig = {
    parentRoute: this.firmSettings,
    parameterizedPath: "/subscription",
  }
  firmSettingsBranding: RouteConfig = {
    parentRoute: this.firmSettings,
    parameterizedPath: "/branding",
  }

  firmAgentInstruction: RouteConfig = {
    parentRoute: this.firmDashboardFirmKnown,
    parameterizedPath: "/agent-instruction",
    moduleName: "Agent Instruction",
    detailsViewAction: DetailsViewAction.Push,
  }

  firmAgentInstructionCheck: RouteConfig = {
    parentRoute: this.firmAgentInstruction,
    parameterizedPath: "/:group/:checkId",
    detailsViewAction: DetailsViewAction.Push,
  }

  // Firm Client management routes (create, configure, upgrade)
  firmClientRoot: RouteConfig = {
    parentRoute: this.firmDashboardFirmKnown,
    parameterizedPath: "/client",
    moduleName: "Add Client",
  }
  firmClientKnown: RouteConfig = {
    parentRoute: this.firmClientRoot,
    parameterizedPath: `/@:${LE_SLUG_PARAM}`,
  }

  // Firm Add Client workflow routes (new & existing LEs)
  firmAddNewClient: RouteConfig = {
    parentRoute: this.firmClientRoot,
    parameterizedPath: "/new",
  }
  firmSetupClient: RouteConfig = {
    parentRoute: this.firmClientKnown,
    parameterizedPath: "/setup",
  }
  firmUpgradeClient: RouteConfig = {
    parentRoute: this.firmClientKnown,
    parameterizedPath: "/upgrade",
  }
  firmResetClient: RouteConfig = {
    parentRoute: this.firmClientKnown,
    parameterizedPath: "/reset",
  }
  firmResetClientSetup: RouteConfig = {
    parentRoute: this.firmResetClient,
    parameterizedPath: "/setup",
  }

  // Legal
  legal = "/legal"
  termsOfService = `${this.legal}/terms-of-service`
  developerTermsOfService = `${this.legal}/developer-terms-of-service`
  privacyPolicy = `${this.legal}/privacy-policy`
  taxPartnerAgreement = `${this.legal}/tax-partner-agreement`

  dashboard = "/dashboard"

  sharedWithMe = {
    parameterizedPath: "/shared-with-me",
    moduleName: "Shared With Me",
  }

  sharedWithMeReportPackages = {
    parameterizedPath: "/reports",
    parentRoute: this.sharedWithMe,
  }

  sharedWithMeCategories = {
    parameterizedPath: "/categories",
    parentRoute: this.sharedWithMe,
  }

  sharedWithMeDepartments = {
    parameterizedPath: "/departments",
    parentRoute: this.sharedWithMe,
  }

  sharedWithMeLocations = {
    parameterizedPath: "/locations",
    parentRoute: this.sharedWithMe,
  }

  sharedWithMeTransactions = {
    parameterizedPath: "/transactions",
    parentRoute: this.sharedWithMe,
  }

  sharedWithMeParties = {
    parameterizedPath: `/:partyRole(${SUPPORTED_PARTY_ROLES_URL_KEYS.join("|")})`,
    parentRoute: this.sharedWithMe,
  }

  // Marketing Redirect Routes

  // These routes intentionally don't have an LE slug to allow use in marketing campaigns
  redirectChecklist = "/checklist"
  redirectAgentInstruction = "/agent-instruction"
  redirectInbox = "/inbox"
  redirectAnalysis = "/analysis"
  redirectReports = "/reports"
  redirectFinancials = "/financials"
  redirectBillPay = "/bill-pay"
  redirectInvoicing = "/invoicing"
  redirectSchedules = "/schedules"
  redirectTransactions = "/transactions"
  redirectReconciliation = "/reconciliations"
  redirectVault = "/vault"
  redirectVendors = "/vendors"
  redirectCustomers = "/customers"
  redirectConnections: RouteConfig = {
    parameterizedPath: "/connections",
    detailsViewAction: DetailsViewAction.Push,
  }
  redirectSettings: RouteConfig = {
    parameterizedPath: "/settings",
    detailsViewAction: DetailsViewAction.Push,
  }

  orgRoot: RouteConfig = {
    parameterizedPath: "/org/@:orgSlug",
    moduleName: "Home",
  }

  // Accountant portal
  legacyPortal: RouteConfig = {
    parameterizedPath: "/portal/:orgSlug",
    moduleName: "Accountant Portal",
  }

  // LegalEntity-level routes (LE Home Dashboard)
  legalEntity: RouteConfig = {
    parameterizedPath: "/@:leSlug",
    moduleName: "Dashboard",
  }

  legalEntityHome: RouteConfig = {
    parameterizedPath: "/home",
    parentRoute: this.legalEntity,
    moduleName: "Dashboard",
    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.Today,
    },
  }

  legalEntityHomeEdit: RouteConfig = {
    parameterizedPath: "/edit",
    parentRoute: this.legalEntityHome,
    moduleName: "Customize",
    detailsViewAction: DetailsViewAction.Push,
  }

  legalEntityHomeEditTab: RouteConfig = {
    parentRoute: this.legalEntityHomeEdit,
    parameterizedPath: `/:tab(${TabNames.join("|")})`,
  }

  transactionDetails: RouteConfig = {
    parameterizedPath: "/transaction/:transactionId",
    parentRoute: this.legalEntity,
    detailsViewAction: DetailsViewAction.Push,
    moduleName: "Transaction Details",
    getSharedObjectInfo: transactionSharedObjectInfo,
  }

  // Used only for requesting comment context and redirecting to the correct role associated with the comment context
  partyDetailsRedirectForComment: RouteConfig = {
    parameterizedPath: "/party/:partyId/comment/:commentId",
    parentRoute: this.legalEntity,
    detailsViewAction: DetailsViewAction.Push,
    getSharedObjectInfo: partySharedObjectInfo,
  }

  partyDetailsRedirectForThread: RouteConfig = {
    parameterizedPath: "/party/:partyId/thread/:threadId",
    parentRoute: this.legalEntity,
    detailsViewAction: DetailsViewAction.Push,
    getSharedObjectInfo: partySharedObjectInfo,
  }

  partyDetails: RouteConfig = {
    parameterizedPath: `/:partyRole(${SUPPORTED_PARTY_ROLES_URL_KEYS.join("|")})/:partyId`,
    parentRoute: this.legalEntity,
    detailsViewAction: DetailsViewAction.Push,
    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.CurrentMonth,
    },
    moduleName: ({ partyRole }) =>
      `${FrontendPartyRole.findByURLKey(partyRole).displayName} Details`,
    getSharedObjectInfo: partySharedObjectInfo,
  }

  partyDetailsCategories: RouteConfig = {
    parameterizedPath: "/categories",
    parentRoute: this.partyDetails,
  }

  partyDetailsTransactions: RouteConfig = {
    parameterizedPath: "/transactions",
    parentRoute: this.partyDetails,
  }

  partyDetailsSchedules: RouteConfig = {
    parameterizedPath: "/schedules",
    parentRoute: this.partyDetails,
  }

  // Analysis
  analysis: RouteConfig = {
    parameterizedPath: "/analysis",
    parentRoute: this.legalEntity,
    moduleName: "Analysis",
    timeParameterOptions: {
      interval: Interval.Month,
    },
  }

  // Expenses (formerly known as Burn)
  expenses: RouteConfig = {
    parentRoute: this.legalEntityHome,
    parameterizedPath: "/expenses",
    moduleName: "Expenses",
  }

  /**
   * @deprecated Redirects to expenses base route
   */
  expenseTransactions: RouteConfig = {
    parameterizedPath: "/transactions",
    parentRoute: this.expenses,
  }

  expensesByCategories: RouteConfig = {
    parameterizedPath: "/categories",
    parentRoute: this.expenses,
  }

  expensesByCategoriesWaterfall: RouteConfig = {
    parameterizedPath: "/waterfall",
    parentRoute: this.expensesByCategories,
  }

  expensesByVendors: RouteConfig = {
    parameterizedPath: "/vendors",
    parentRoute: this.expenses,
  }

  expensesByVendorsWaterfall: RouteConfig = {
    parameterizedPath: "/waterfall",
    parentRoute: this.expensesByVendors,
  }

  // Revenue
  revenue: RouteConfig = {
    parentRoute: this.legalEntityHome,
    parameterizedPath: "/revenue",
    moduleName: "Revenue",
  }

  /**
   * @deprecated Redirects to revenue base route
   */
  revenueTransactions: RouteConfig = {
    parentRoute: this.revenue,
    parameterizedPath: "/transactions",
  }

  // Revenue By Category
  revenueByCategories: RouteConfig = {
    parentRoute: this.revenue,
    parameterizedPath: "/categories",
  }

  revenueByCategoriesWaterfall: RouteConfig = {
    parentRoute: this.revenueByCategories,
    parameterizedPath: "/waterfall",
  }

  // Revenue By Customers
  revenueByCustomers: RouteConfig = {
    parentRoute: this.revenue,
    parameterizedPath: "/customers",
  }

  revenueByCustomersWaterfall: RouteConfig = {
    parentRoute: this.revenueByCustomers,
    parameterizedPath: "/waterfall",
  }

  // Cost of Goods Sold
  cogs: RouteConfig = {
    parentRoute: this.legalEntityHome,
    parameterizedPath: "/cost-of-revenue",
    moduleName: "Cost of Revenue",
  }

  /**
   * @deprecated Redirects to cogs base route
   */
  cogsTransactions: RouteConfig = {
    parentRoute: this.cogs,
    parameterizedPath: "/transactions",
  }

  cogsByCategories: RouteConfig = {
    parentRoute: this.cogs,
    parameterizedPath: "/categories",
  }

  cogsByCategoriesWaterfall: RouteConfig = {
    parentRoute: this.cogsByCategories,
    parameterizedPath: "/waterfall",
  }

  cogsBySuppliers: RouteConfig = {
    parentRoute: this.cogs,
    parameterizedPath: "/suppliers",
  }

  cogsBySuppliersWaterfall: RouteConfig = {
    parentRoute: this.cogsBySuppliers,
    parameterizedPath: "/waterfall",
  }

  // Custom Dashboards
  customDashboard: RouteConfig = {
    parentRoute: this.legalEntityHome,
    parameterizedPath: "/section/:sectionId",
    moduleName: "Dashboard",
  }

  customDashboardEdit: RouteConfig = {
    parameterizedPath: "/edit",
    parentRoute: this.customDashboard,
    moduleName: "Customize",
    detailsViewAction: DetailsViewAction.Push,
  }

  customDashboardEditTab: RouteConfig = {
    parentRoute: this.customDashboardEdit,
    parameterizedPath: `/:tab(${TabNames.join("|")})`,
  }

  // Reports
  reports: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/reports",
    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.PreviousMonth,
    },
    moduleName: "Reports",
  }

  reportsGenerate: RouteConfig = {
    parentRoute: this.reports,
    parameterizedPath: "/new",
  }

  // Scheduled Reports Setup
  reportsSchedule: RouteConfig = {
    parentRoute: this.reports,
    parameterizedPath: "/schedule",
    timeParameterOptions: undefined,
  }

  // deprecated, will redirect to new reportsStatement
  legacyCategoryReview: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/review",
    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.AtLeast15Days,
    },
    moduleName: "Review",
  }

  // Statement Review (P&L, Balance Sheet, Cash flows)
  financials: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: `/financials/:kind(${[
      ReportKind.ProfitAndLoss,
      ReportKind.BalanceSheet,
      ReportKind.CashFlow,
      ReportKind.APAging,
      ReportKind.ARAging,
    ].join("|")})`,
    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.PreviousMonth,
    },
    moduleName: "Financials",
  }

  reportPackage: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/report/:packageId",
    detailsViewAction: DetailsViewAction.Push,
    getSharedObjectInfo: reportSharedObjectInfo,
    moduleName: "Report",
  }

  reportPackageVersion: RouteConfig = {
    parentRoute: this.reportPackage,
    parameterizedPath: "/v/:versionId",
    detailsViewAction: DetailsViewAction.Push,
    getSharedObjectInfo: reportSharedObjectInfo,
    moduleName: "Report",
  }

  reportPackageSharing: RouteConfig = {
    parentRoute: this.reportPackage,
    parameterizedPath: "/share",
    detailsViewAction: DetailsViewAction.Push,
    getSharedObjectInfo: reportSharedObjectInfo,
  }

  reportPackageVersionSharing: RouteConfig = {
    parentRoute: this.reportPackageVersion,
    parameterizedPath: "/share",
    detailsViewAction: DetailsViewAction.Push,
    getSharedObjectInfo: reportSharedObjectInfo,
  }

  reportPackageEditVersion: RouteConfig = {
    parentRoute: this.reportPackage,
    parameterizedPath: "/edit/:versionId",
    detailsViewAction: DetailsViewAction.Push,
    getSharedObjectInfo: reportSharedObjectInfo,
  }

  reportPackageVersionOptions: RouteConfig = {
    parentRoute: this.reportPackageEditVersion,
    parameterizedPath: "/options",
    detailsViewAction: DetailsViewAction.Push,
    getSharedObjectInfo: reportSharedObjectInfo,
  }

  reportStatementOptions: RouteConfig = {
    parentRoute: this.reportPackageVersionOptions,
    parameterizedPath: "/document/:documentId",
  }

  reportLayoutOptions: RouteConfig = {
    parentRoute: this.reportPackageVersionOptions,
    parameterizedPath: "/layout/:sectionId",
    detailsViewAction: DetailsViewAction.Push,
    getSharedObjectInfo: reportSharedObjectInfo,
  }

  reportAddLayoutComponent: RouteConfig = {
    parentRoute: this.reportPackageEditVersion,
    parameterizedPath: "/layout/:sectionId",
    detailsViewAction: DetailsViewAction.Push,
    getSharedObjectInfo: reportSharedObjectInfo,
  }

  // Boost
  qualityCheck: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/quality",
    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.AtLeast15Days,
    },
    moduleName: "Quality Check",
  }

  qualityCheckSettings: RouteConfig = {
    parentRoute: this.qualityCheck,
    parameterizedPath: "/settings",
  }

  boost: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/boost",
    moduleName: "Quality Check",
    timeParameterOptions: {
      interval: Interval.Month,
    },
  }

  // Transaction Review
  transactionReview: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/transaction-review",
    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.AtLeast15Days,
    },
    moduleName: "Transaction Review",
  }

  transactionReviewItems: RouteConfig = {
    parentRoute: this.transactionReview,
    parameterizedPath: "/new",
  }

  transactionReviewPending: RouteConfig = {
    parentRoute: this.transactionReview,
    parameterizedPath: "/pending",
  }

  transactionReviewReplies: RouteConfig = {
    parentRoute: this.transactionReview,
    parameterizedPath: "/replies",
  }

  transactionReviewSettings: RouteConfig = {
    parentRoute: this.transactionReview,
    parameterizedPath: "/settings",
  }

  checklist: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/checklist",
    moduleName: "Checklist",
    detailsViewAction: DetailsViewAction.Push,
  }

  checklistEarliest: RouteConfig = {
    parentRoute: this.checklist,
    parameterizedPath: "/earliest/:section",
  }

  checklistCategory: RouteConfig = {
    parentRoute: this.checklist,
    parameterizedPath: "/:section",
  }

  checklistCloseBooks: RouteConfig = {
    parentRoute: this.checklistCategory,
    parameterizedPath: "",
    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.Today,
    },
  }

  agentInstruction: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/agent-instruction",
    moduleName: "Agent Instruction",
    detailsViewAction: DetailsViewAction.Push,
  }

  agentInstructionCheck: RouteConfig = {
    parentRoute: this.agentInstruction,
    parameterizedPath: "/:group/:checkId",
    detailsViewAction: DetailsViewAction.Push,
  }

  checklistTask: RouteConfig = {
    parentRoute: this.checklistCategory,
    parameterizedPath: "/:taskKind/:taskId",
  }

  checklistCloseBooksTask: RouteConfig = {
    parentRoute: this.checklistTask,
    parameterizedPath: "",
    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.Today,
    },
  }

  /**
   * @deprecated Use checklist
   */
  inbox: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/inbox",
    moduleName: "Checklist",
  }

  /**
   * @deprecated Use clientTransactionReview
   */
  actionItems: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/action-items",
  }

  /**
   * @deprecated use routes.clientTransactionReview instead
   */
  actionItem: RouteConfig = {
    parentRoute: this.actionItems,
    parameterizedPath: "/:workItemId",
  }

  actionItemsList: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/action-items-list",
    moduleName: "Transaction Review",
  }

  clientTransactionReview: RouteConfig = {
    parentRoute: this.actionItemsList,
    parameterizedPath: "/transaction-review",
    // mobile/tablet renders a Content view but desktop renders a Details view
    ...(isMobile ? undefined : { detailsViewAction: DetailsViewAction.Push }),
  }

  clientTransactionReviewItem: RouteConfig = {
    parentRoute: this.clientTransactionReview,
    parameterizedPath: "/:workItemId",
    detailsViewAction: DetailsViewAction.Push,
  }

  // Client Portal (LEGACY)
  clientPortal: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/portal",
    moduleName: "Client Portal",
  }

  // Used in emails (LEGACY)
  clientPortalActionItems: RouteConfig = {
    parentRoute: this.clientPortal,
    parameterizedPath: "/action-items",
  }

  // Legal Entity Settings
  legalEntitySettings: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/settings",
    moduleName: "Settings",
    detailsViewAction: DetailsViewAction.Push,
  }

  businessVerificationSettings: RouteConfig = {
    parentRoute: this.legalEntitySettings,
    parameterizedPath: "/business-verification",
    moduleName: "Business Verification",
  }

  subscriptionSettings: RouteConfig = {
    parentRoute: this.legalEntitySettings,
    parameterizedPath: "/subscription",
  }

  teamMemberSettings: RouteConfig = {
    parentRoute: this.legalEntitySettings,
    parameterizedPath: "/team-members",
  }

  addTeamMember: RouteConfig = {
    parentRoute: this.teamMemberSettings,
    parameterizedPath: "/add",
  }

  accountingSettings: RouteConfig = {
    parentRoute: this.legalEntitySettings,
    parameterizedPath: "/accounting",
  }

  objectMappingSettings: RouteConfig = {
    parentRoute: this.accountingSettings,
    parameterizedPath: "/object-mapping",
  }

  billPaySettings: RouteConfig = {
    parentRoute: this.legalEntitySettings,
    parameterizedPath: "/bill-pay",
  }

  billPayLedgerIntegrationSettings: RouteConfig = {
    parentRoute: this.billPaySettings,
    parameterizedPath: "/ledger-integration",
  }

  invoicingSettings: RouteConfig = {
    parentRoute: this.legalEntitySettings,
    parameterizedPath: "/invoicing",
  }

  invoicingLedgerIntegrationSettings: RouteConfig = {
    parentRoute: this.invoicingSettings,
    parameterizedPath: "/ledger-integration",
  }

  invoicingProfileSettings: RouteConfig = {
    parentRoute: this.invoicingSettings,
    parameterizedPath: "/profile",
  }

  // @deprecated -- legacy route replaced by collectSettings
  getPaidSettings: RouteConfig = {
    parentRoute: this.legalEntitySettings,
    parameterizedPath: "/get-paid",
  }

  collectSettings: RouteConfig = {
    parentRoute: this.legalEntitySettings,
    parameterizedPath: "/collect",
  }

  walletSettings: RouteConfig = {
    parentRoute: this.legalEntitySettings,
    parameterizedPath: "/wallet",
  }

  integrationSettings: RouteConfig = {
    parentRoute: this.legalEntitySettings,
    parameterizedPath: "/integrations",
  }

  // @deprecated legacy route, redirects to legalEntitySettings
  legalEntityIdentityVerification: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/identity-verification",
    detailsViewAction: DetailsViewAction.Push,
  }

  // @deprecated legacy route replaced by collect
  getPaid: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/get-paid",
    moduleName: "Collect",
  }

  collect: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/collect",
    moduleName: "Collect",
  }

  // Departments
  departmentDetails: RouteConfig = {
    parameterizedPath: "/department/:departmentId",
    parentRoute: this.legalEntity,
    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.CurrentMonth,
    },
    detailsViewAction: DetailsViewAction.Push,
    moduleName: "Department",
    getSharedObjectInfo: departmentSharedObjectInfo,
  }

  departmentDetailsTransactions: RouteConfig = {
    parentRoute: this.departmentDetails,
    parameterizedPath: "/transactions",
  }

  departmentDetailsSubdepartments: RouteConfig = {
    parentRoute: this.departmentDetails,
    parameterizedPath: "/subdepartments",
  }

  departmentDetailsComments: RouteConfig = {
    parentRoute: this.departmentDetails,
    parameterizedPath: "/comments",
  }

  // Locations
  locationDetails: RouteConfig = {
    parameterizedPath: "/location/:locationId",
    parentRoute: this.legalEntity,
    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.CurrentMonth,
    },
    detailsViewAction: DetailsViewAction.Push,
    moduleName: "Location",
    getSharedObjectInfo: locationSharedObjectInfo,
  }

  locationDetailsTransactions: RouteConfig = {
    parentRoute: this.locationDetails,
    parameterizedPath: "/transactions",
  }

  locationDetailsSublocations: RouteConfig = {
    parentRoute: this.locationDetails,
    parameterizedPath: "/sublocations",
  }

  locationDetailsComments: RouteConfig = {
    parentRoute: this.locationDetails,
    parameterizedPath: "/comments",
  }

  // Category
  categoryDetails: RouteConfig = {
    parameterizedPath: "/category/:categoryId",
    parentRoute: this.legalEntity,
    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.CurrentMonth,
    },
    detailsViewAction: DetailsViewAction.Push,
    moduleName: "Category Details",
    getSharedObjectInfo: categorySharedObjectInfo,
  }

  categoryDetailsTransactions: RouteConfig = {
    parentRoute: this.categoryDetails,
    parameterizedPath: "/transactions",
  }

  categoryDetailsPartyRole: RouteConfig = {
    parentRoute: this.categoryDetails,
    parameterizedPath: `/:partyRole(${SUPPORTED_PARTY_ROLES_URL_KEYS.join("|")})`,
  }

  categoryDetailsPartyRoleOtherSide: RouteConfig = {
    parentRoute: this.categoryDetailsPartyRole,
    parameterizedPath: "/other-side",
  }

  categoryDetailsPartyRoleWaterfall: RouteConfig = {
    parentRoute: this.categoryDetailsPartyRole,
    parameterizedPath: "/waterfall",
  }

  categoryDetailsPartyRoleWaterfallOtherSide: RouteConfig = {
    parentRoute: this.categoryDetailsPartyRoleWaterfall,
    parameterizedPath: "/other-side",
  }

  categoryDetailsSubcategories: RouteConfig = {
    parentRoute: this.categoryDetails,
    parameterizedPath: "/subcategories",
  }

  categoryDetailsDepartments: RouteConfig = {
    parentRoute: this.categoryDetails,
    parameterizedPath: "/departments",
  }

  categoryDetailsLocations: RouteConfig = {
    parentRoute: this.categoryDetails,
    parameterizedPath: "/locations",
  }

  categoryDetailsSchedules: RouteConfig = {
    parentRoute: this.categoryDetails,
    parameterizedPath: "/schedules",
  }

  categoryDetailsComments: RouteConfig = {
    parentRoute: this.categoryDetails,
    parameterizedPath: "/comments",
  }

  // Metrics & Summaries Details views
  layoutComponentDetails: RouteConfig = {
    parameterizedPath: "/chart/:configType",
    parentRoute: this.legalEntity,
    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.CurrentMonth,
    },
    detailsViewAction: DetailsViewAction.Push,
    moduleName: "Chart",
  }

  // Affiliates (e.g. Accountants)
  // TODO: Add redirects for legacy portal routes

  /**
   * @deprecated use firmSettingsEmployeesSettings
   */
  organizationTeams: RouteConfig = {
    parentRoute: this.legacyPortal,
    parameterizedPath: "/teams",
    moduleName: "Teams",
  }

  /**
   * @deprecated use firmSettings
   */
  organizationSettings: RouteConfig = {
    parentRoute: this.legacyPortal,
    parameterizedPath: "/settings",
    moduleName: "Settings",
  }

  organizationBilling: RouteConfig = {
    parentRoute: this.legacyPortal,
    parameterizedPath: "/billing",
    moduleName: "Current Plan",
    detailsViewAction: DetailsViewAction.Push,
  }

  organizationBillingUpgrade: RouteConfig = {
    parentRoute: this.organizationBilling,
    parameterizedPath: "/upgrade",
    moduleName: "Billing Plans",
    detailsViewAction: DetailsViewAction.Push,
  }

  organizationBillingCheckout: RouteConfig = {
    parentRoute: this.organizationBilling,
    parameterizedPath: "/checkout",
    detailsViewAction: DetailsViewAction.Push,
    moduleName: "Checkout",
  }

  /**
   * @deprecated use firmSettingsClient
   */
  organizationSettingsClients: RouteConfig = {
    parentRoute: this.organizationSettings,
    parameterizedPath: "/clients",
  }

  /**
   * @deprecated use firmSettingsClient
   */
  organizationSettingsClient: RouteConfig = {
    parentRoute: this.organizationSettingsClients,
    parameterizedPath: `${this.legacyPortal.parameterizedPath}/settings/client/:leSlug`,
    overrideParentParameterizedPath: true,
  }

  /**
   * @deprecated use firmSettingsEmployeesSettings
   */
  organizationSettingsEmployees: RouteConfig = {
    parentRoute: this.organizationSettings,
    parameterizedPath: "/employees",
  }

  /**
   * @deprecated use firmSettingsEmployeeSettings
   */
  organizationSettingsEmployee: RouteConfig = {
    parentRoute: this.organizationSettingsEmployees,
    parameterizedPath: `${this.legacyPortal.parameterizedPath}/settings/employee/:employeeId`,
    overrideParentParameterizedPath: true,
  }

  /**
   * @deprecated use firmSettings
   */
  organizationFirmSettings: RouteConfig = {
    parentRoute: this.organizationSettings,
    parameterizedPath: "/firm",
  }

  // Connections
  connections: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/connections",
    detailsViewAction: DetailsViewAction.Push,
    moduleName: "Connections",
  }

  createConnection: RouteConfig = {
    parentRoute: this.connections,
    detailsViewAction: DetailsViewAction.Protected,
    parameterizedPath: "/create",
  }

  createStripeConnection: RouteConfig = {
    parentRoute: this.createConnection,
    parameterizedPath: "/stripe",
  }

  updateConnection: RouteConfig = {
    parentRoute: this.createConnection,
    parameterizedPath: "/:connectionId",
  }

  manageConnection: RouteConfig = {
    parentRoute: this.connections,
    parameterizedPath: "/manage/:connectionId",
  }

  allConnections: RouteConfig = {
    parentRoute: this.connections,
    parameterizedPath: "/all",
  }

  // Category tab routes for connection templates
  banksAndCardsConnections: RouteConfig = {
    parentRoute: this.connections,
    parameterizedPath: "/banks-and-cards",
  }

  // Currently just a fallthrough route for all connections
  paymentsConnections: RouteConfig = {
    parentRoute: this.connections,
    parameterizedPath: "/payments",
  }

  // Currently just a fallthrough route for all connections
  payrollConnections: RouteConfig = {
    parentRoute: this.connections,
    parameterizedPath: "/payroll",
  }

  billsAndExpensesConnections: RouteConfig = {
    parentRoute: this.connections,
    parameterizedPath: "/bills-and-expenses",
  }

  ecommerceConnections: RouteConfig = {
    parentRoute: this.connections,
    parameterizedPath: "/ecommerce",
  }

  reportingConnections: RouteConfig = {
    parentRoute: this.connections,
    parameterizedPath: "/reporting",
  }

  // Falls through to all connections but monitored by DeepLinkModal
  gustoConnection: RouteConfig = {
    parentRoute: this.connections,
    parameterizedPath: "/gusto",
  }

  // Falls through to all connections but monitored by DeepLinkModal
  rampConnection: RouteConfig = {
    parentRoute: this.connections,
    parameterizedPath: "/ramp",
  }

  // Falls through to all connections but monitored by DeepLinkModal
  stripeConnection: RouteConfig = {
    parentRoute: this.connections,
    parameterizedPath: "/stripe",
  }

  createNewConnection: RouteConfig = {
    parentRoute: this.allConnections,
    parameterizedPath: "/:connectorId",
  }

  // Connectors
  connectorOauth: RouteConfig = {
    detailsViewAction: DetailsViewAction.Protected,
    parameterizedPath: "/oauth",
    moduleName: "Connectors",
  }

  stripeOauth: RouteConfig = {
    parentRoute: this.connectorOauth,
    parameterizedPath: "/stripe",
  }

  rampOauth: RouteConfig = {
    parentRoute: this.connectorOauth,
    parameterizedPath: "/ramp", // doesn't render anything, monitored by OAuthConnectButton popup
  }

  gustoOauth: RouteConfig = {
    parentRoute: this.connectorOauth,
    parameterizedPath: "/gusto", // doesn't render anything, monitored by OAuthConnectButton popup
  }

  // Pay
  oldPayBills: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/pay-bills",
    moduleName: "Bill Pay",
  }

  billPay: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/bill-pay",
    moduleName: "Bills",
  }

  bills: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/bills",
    moduleName: "Bills",
  }

  billDraft: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/bill/draft",
    detailsViewAction: DetailsViewAction.Push,
    moduleName: "Bill",
  }

  billDraftComments: RouteConfig = {
    parentRoute: this.billDraft,
    parameterizedPath: "/comments",
    detailsViewAction: DetailsViewAction.Push,
  }

  billDraftActivity: RouteConfig = {
    parentRoute: this.billDraft,
    parameterizedPath: "/activity",
    detailsViewAction: DetailsViewAction.Push,
  }

  billDetails: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/bill/:billId",
    detailsViewAction: DetailsViewAction.Push,
    moduleName: "Bill",
  }

  billDetailsVendor: RouteConfig = {
    parentRoute: this.billDetails,
    parameterizedPath: "/vendor",
  }

  billDetailsComments: RouteConfig = {
    parentRoute: this.billDetails,
    parameterizedPath: "/comments",
  }

  billDetailsActivity: RouteConfig = {
    parentRoute: this.billDetails,
    parameterizedPath: "/activity",
  }

  billDetailsPayment: RouteConfig = {
    parentRoute: this.billDetails,
    parameterizedPath: "/payment",
    moduleName: "Bill Details",
  }

  billDetailsLineItems: RouteConfig = {
    parentRoute: this.billDetails,
    parameterizedPath: "/line-items",
  }

  billPaymentDetails: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/bill-payment/:billPaymentId",
    moduleName: "Bill Payment",
    detailsViewAction: DetailsViewAction.Push,
  }

  billPaymentDetailsComments: RouteConfig = {
    parentRoute: this.billPaymentDetails,
    parameterizedPath: "/comments",
  }

  billPaymentDetailsBillInfo: RouteConfig = {
    parentRoute: this.billPaymentDetails,
    parameterizedPath: "/bill-info",
  }

  collectFunds: RouteConfig = {
    parameterizedPath: "/collect/:id",
  }

  // Invoices
  invoicing: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/invoicing",
    moduleName: "Invoices",
  }

  productsServices: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/products-services",
    moduleName: "Products & Services",
  }

  productsServicesCreate: RouteConfig = {
    parentRoute: this.productsServices,
    parameterizedPath: "/create",
    moduleName: "Create Product",
    detailsViewAction: DetailsViewAction.Push,
  }

  productsServicesDetails: RouteConfig = {
    parentRoute: this.productsServices,
    parameterizedPath: "/:productId",
    moduleName: "Product",
    detailsViewAction: DetailsViewAction.Push,
  }

  productsServicesDetailsEdit: RouteConfig = {
    parentRoute: this.productsServicesDetails,
    parameterizedPath: "/edit",
    moduleName: "Edit Product",
    detailsViewAction: DetailsViewAction.Push,
  }

  draftInvoice: RouteConfig = {
    moduleName: "Invoice",
    parentRoute: this.legalEntity,
    parameterizedPath: "/invoice/draft",
    detailsViewAction: DetailsViewAction.Push,
  }

  draftInvoiceCreate: RouteConfig = {
    parentRoute: this.draftInvoice,
    parameterizedPath: "/create",
    detailsViewAction: DetailsViewAction.Push,
  }

  draftInvoiceDetails: RouteConfig = {
    moduleName: "Invoice",
    parentRoute: this.draftInvoice,
    parameterizedPath: "/:id",
    detailsViewAction: DetailsViewAction.Push,
  }

  /** Ledger invoice detail — renders invoice via transaction data, separate from TransactionDetail */
  ledgerInvoiceDetail: RouteConfig = {
    moduleName: "Invoice",
    parentRoute: this.invoicing,
    parameterizedPath: "/:transactionId",
    detailsViewAction: DetailsViewAction.Push,
    getSharedObjectInfo: transactionSharedObjectInfo,
  }

  sentInvoiceDetails: RouteConfig = {
    moduleName: "Invoice",
    parentRoute: this.legalEntity,
    parameterizedPath: "/invoice/:id",
    detailsViewAction: DetailsViewAction.Push,
  }

  /**
   * Send screen for an invoice that has been Created (approved) but not yet
   * sent. Reuses the draft chrome/molecule but lives at a non-draft URL since
   * the invoice itself is past the Draft state at this point.
   */
  sendInvoice: RouteConfig = {
    moduleName: "Invoice",
    parentRoute: this.legalEntity,
    parameterizedPath: "/invoice/:id/send",
    detailsViewAction: DetailsViewAction.Push,
  }

  // Draft invoice sidebar tab sub-routes
  draftInvoiceComments: RouteConfig = {
    parentRoute: this.draftInvoiceDetails,
    parameterizedPath: "/comments",
    detailsViewAction: DetailsViewAction.Push,
  }

  draftInvoiceActivityLog: RouteConfig = {
    parentRoute: this.draftInvoiceDetails,
    parameterizedPath: "/activity-log",
    detailsViewAction: DetailsViewAction.Push,
  }

  // Ledger invoice detail sidebar tab sub-routes
  ledgerInvoiceDetailComments: RouteConfig = {
    parentRoute: this.ledgerInvoiceDetail,
    parameterizedPath: "/comments",
    detailsViewAction: DetailsViewAction.Push,
  }

  ledgerInvoiceDetailActivityLog: RouteConfig = {
    parentRoute: this.ledgerInvoiceDetail,
    parameterizedPath: "/activity-log",
    detailsViewAction: DetailsViewAction.Push,
  }

  // Sent invoice sidebar tab sub-routes
  sentInvoiceComments: RouteConfig = {
    parentRoute: this.sentInvoiceDetails,
    parameterizedPath: "/comments",
    detailsViewAction: DetailsViewAction.Push,
  }

  sentInvoiceActivityLog: RouteConfig = {
    parentRoute: this.sentInvoiceDetails,
    parameterizedPath: "/activity-log",
    detailsViewAction: DetailsViewAction.Push,
  }

  // Send invoice sidebar tab sub-routes
  sendInvoiceComments: RouteConfig = {
    parentRoute: this.sendInvoice,
    parameterizedPath: "/comments",
    detailsViewAction: DetailsViewAction.Push,
  }

  sendInvoiceActivityLog: RouteConfig = {
    parentRoute: this.sendInvoice,
    parameterizedPath: "/activity-log",
    detailsViewAction: DetailsViewAction.Push,
  }

  sentInvoiceDetailsDelivery: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/invoice/:id/delivery",
    detailsViewAction: DetailsViewAction.Replace,
  }

  sentInvoiceDetailsCustomer: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/invoice/:id/customer",
    detailsViewAction: DetailsViewAction.Replace,
  }

  sentInvoiceDetailsPaymentLink: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/invoice/:id/payment-link",
    detailsViewAction: DetailsViewAction.Replace,
  }

  sentInvoiceDetailsAttachments: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/invoice/:id/attachments",
    detailsViewAction: DetailsViewAction.Replace,
  }

  publicInvoice: RouteConfig = {
    parameterizedPath: "/invoice/:id",
  }

  // Schedules
  schedules: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/schedules",
    moduleName: "Schedules",
    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.CurrentMonth,
    },
  }

  scheduleDetails: RouteConfig = {
    parentRoute: this.schedules,
    parameterizedPath: "/:scheduleId",
    moduleName: "Schedule",
    detailsViewAction: DetailsViewAction.Push,
    timeParameterOptions: undefined,
  }

  scheduleNew: RouteConfig = {
    parameterizedPath: "/new",
    parentRoute: this.schedules,
    moduleName: "Schedule",
    detailsViewAction: DetailsViewAction.Push,
    timeParameterOptions: undefined,
  }

  // Accounting
  accounting: RouteConfig = {
    parameterizedPath: "/transactions",
    parentRoute: this.legalEntity,
    moduleName: "Ledger",

    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.CurrentMonth,
    },
  }

  banksAndCards: RouteConfig = {
    parameterizedPath: "/banks-and-cards",
    parentRoute: this.legalEntity,
    moduleName: "Banks & Cards",

    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.CurrentMonth,
    },
  }

  /** Create a new invoice as a ledger transaction. No time params needed. */
  accountingInvoiceCreate: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/transactions/new",
    detailsViewAction: DetailsViewAction.Push,
    getSharedObjectInfo: transactionSharedObjectInfo,
  }

  accountingDetail: RouteConfig = {
    parentRoute: this.accounting,
    parameterizedPath: "/new",
    detailsViewAction: DetailsViewAction.Push,
    getSharedObjectInfo: transactionSharedObjectInfo,

    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.CurrentMonth,
      ignoreContextOrigin: true,
    },
  }

  accountingDetailEdit: RouteConfig = {
    parentRoute: this.accounting,
    parameterizedPath: "/:transactionId",
    detailsViewAction: DetailsViewAction.Push,
    getSharedObjectInfo: transactionSharedObjectInfo,
    timeParameterOptions: undefined,
  }

  accountingDetailComments: RouteConfig = {
    parentRoute: this.accountingDetailEdit,
    parameterizedPath: "/comments",
    detailsViewAction: DetailsViewAction.Push,
  }

  accountingDetailPayable: RouteConfig = {
    parentRoute: this.accountingDetailEdit,
    parameterizedPath: "/payable",
    detailsViewAction: DetailsViewAction.Push,
  }

  accountingDetailInsights: RouteConfig = {
    parentRoute: this.accountingDetailEdit,
    parameterizedPath: "/insights",
    detailsViewAction: DetailsViewAction.Push,
  }

  accountingDetailActivityLog: RouteConfig = {
    parentRoute: this.accountingDetailEdit,
    parameterizedPath: "/activity-log",
    detailsViewAction: DetailsViewAction.Push,
  }

  accountingDetailLedgerTransactionId: RouteConfig = {
    parentRoute: this.accountingDetail,
    parameterizedPath: "/ledger/:ledgerTransactionId",
    detailsViewAction: DetailsViewAction.Push,
  }

  accountingDetailLedgerTransactionIdComment: RouteConfig = {
    parentRoute: this.accountingDetailLedgerTransactionId,
    parameterizedPath: "/comments",
    detailsViewAction: DetailsViewAction.Push,
  }

  accountingDetailDataSource: RouteConfig = {
    parentRoute: this.accountingDetail,
    parameterizedPath: "/:dataSourceId/:externalId/:externalLineItemId",
    detailsViewAction: DetailsViewAction.Push,
  }

  accountingDetailDataSourceComment: RouteConfig = {
    parentRoute: this.accountingDetailDataSource,
    parameterizedPath: "/comments",
    detailsViewAction: DetailsViewAction.Push,
  }

  // Deprecated Accounting Routes (here for redirect purposes)
  accountingDeprecated: RouteConfig = {
    parameterizedPath: "/accounting",
    parentRoute: this.legalEntity,

    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.CurrentMonth,
    },
  }

  accountingTransactionDashboardDeprecated: RouteConfig = {
    parameterizedPath: "/transaction-dashboard",
    parentRoute: this.accountingDeprecated,
    moduleName: "Transaction Dashboard",

    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.CurrentMonth,
    },
  }

  accountingDetailsDeprecated: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/accounting-details",
    detailsViewAction: DetailsViewAction.Push,
  }

  accountingTransactionDetailDeprecated: RouteConfig = {
    parentRoute: this.accountingDetailsDeprecated,
    parameterizedPath: "/transaction-detail",
    detailsViewAction: DetailsViewAction.Push,
    getSharedObjectInfo: transactionSharedObjectInfo,
  }

  accountingTransactionDetailEditRemoved: RouteConfig = {
    parentRoute: this.accountingTransactionDetailDeprecated,
    parameterizedPath: "/:transactionId",
    detailsViewAction: DetailsViewAction.Push,
  }

  accountingTransactionDetailCommentsRemoved: RouteConfig = {
    parentRoute: this.accountingTransactionDetailEditRemoved,
    parameterizedPath: "/comments",
    detailsViewAction: DetailsViewAction.Push,
  }

  accountingTransactionDetailDataSourceRemoved: RouteConfig = {
    parentRoute: this.accountingTransactionDetailDeprecated,
    parameterizedPath: "/:dataSourceId/:externalId/:externalLineItemId",
    detailsViewAction: DetailsViewAction.Push,
  }

  accountingTransactionDetailDataSourceCommentRemoved: RouteConfig = {
    parentRoute: this.accountingTransactionDetailDataSourceRemoved,
    parameterizedPath: "/comments",
    detailsViewAction: DetailsViewAction.Push,
  }

  accountingTransactionDetailLedgerTransactionIdRemoved: RouteConfig = {
    parentRoute: this.accountingTransactionDetailDeprecated,
    parameterizedPath: "/ledger/:ledgerTransactionId",
    detailsViewAction: DetailsViewAction.Push,
  }

  accountingTransactionDetailLedgerTransactionIdCommentRemoved: RouteConfig = {
    parentRoute: this.accountingTransactionDetailLedgerTransactionIdRemoved,
    parameterizedPath: "/comments",
    detailsViewAction: DetailsViewAction.Push,
  }

  // Vendor Directory
  vendorDirectory: RouteConfig = {
    parameterizedPath: "/vendors",
    parentRoute: this.legalEntity,
    moduleName: "Vendors",

    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.CurrentMonth,
    },
  }

  customerDirectory: RouteConfig = {
    parameterizedPath: "/customers",
    parentRoute: this.legalEntity,
    moduleName: "Customers",

    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.CurrentMonth,
    },
  }

  // Vault
  vault: RouteConfig = {
    parameterizedPath: "/vault",
    parentRoute: this.legalEntity,
    moduleName: "Vault",
  }

  vaultFile: RouteConfig = {
    parameterizedPath: "/file/:fileId",
    parentRoute: this.vault,
    detailsViewAction: DetailsViewAction.Push,
  }

  // Reconciliations
  reconciliations: RouteConfig = {
    parameterizedPath: "/reconciliations",
    parentRoute: this.legalEntity,
    moduleName: "Reconciliations",

    timeParameterOptions: {
      interval: Interval.Month,
      defaultIntervalOrigin: RouteDefaultIntervalOrigin.PreviousMonth,
    },
  }

  reconciliation: RouteConfig = {
    parameterizedPath: "/reconciliation/:reconciliationId",
    parentRoute: this.legalEntity,
    detailsViewAction: DetailsViewAction.Push,
  }

  viewOnlyReconciliation: RouteConfig = {
    parameterizedPath: "/reconciliation/:reconciliationId/view",
    parentRoute: this.legalEntity,
    detailsViewAction: DetailsViewAction.Push,
  }

  // Category Manager
  categoryManager: RouteConfig = {
    parameterizedPath: "/category-manager",
    parentRoute: this.legalEntity,
    moduleName: "Category Manager",
    detailsViewAction: DetailsViewAction.Push,
  }

  categoryManagerView: RouteConfig = {
    parameterizedPath: "/:categoryId",
    parentRoute: this.categoryManager,
    detailsViewAction: DetailsViewAction.Push,
  }

  categoryManagerEdit: RouteConfig = {
    parameterizedPath: "/:categoryId/edit",
    parentRoute: this.categoryManager,
    detailsViewAction: DetailsViewAction.Push,
  }

  categoryManagerNew: RouteConfig = {
    parameterizedPath: "/new",
    parentRoute: this.categoryManager,
    detailsViewAction: DetailsViewAction.Push,
  }

  // Dimensions Manager
  dimensionManager: RouteConfig = {
    parameterizedPath: `/dimension/:kind(${DIMENSION_KINDS.join("|")})`,
    parentRoute: this.legalEntity,
    moduleName: ({ kind }) => {
      if (kind && isDimensionKind(kind)) {
        return pluralizeDimension(kind)
      }
      return "Dimensions"
    },
    detailsViewAction: DetailsViewAction.Push,
  }

  dimensionManagerView: RouteConfig = {
    parameterizedPath: "/:entityId",
    parentRoute: this.dimensionManager,
  }

  dimensionManagerEdit: RouteConfig = {
    parameterizedPath: "/:entityId/edit",
    parentRoute: this.dimensionManager,
  }

  dimensionManagerNew: RouteConfig = {
    parameterizedPath: "/new",
    parentRoute: this.dimensionManager,
  }

  dimensionManagerImport: RouteConfig = {
    parameterizedPath: "/import/:source",
    parentRoute: this.dimensionManager,
  }

  // DEVELOPERS DEVELOPERS DEVELOPERS!

  legalEntityDeveloperRoot: RouteConfig = {
    parentRoute: this.legalEntity,
    parameterizedPath: "/developer",
    moduleName: "Developer",
  }

  legalEntityDeveloperApp: RouteConfig = {
    parentRoute: this.legalEntityDeveloperRoot,
    parameterizedPath: "/:id",
    moduleName: "Manage App",
    detailsViewAction: DetailsViewAction.Push,
  }

  legalEntityDeveloperAppConfiguration: RouteConfig = {
    parentRoute: this.legalEntityDeveloperApp,
    parameterizedPath: "/configuration",
    detailsViewAction: DetailsViewAction.Push,
  }

  legalEntityDeveloperAppKeys: RouteConfig = {
    parentRoute: this.legalEntityDeveloperApp,
    parameterizedPath: "/keys",
    detailsViewAction: DetailsViewAction.Push,
  }

  firmDeveloperRoot: RouteConfig = {
    parentRoute: this.firmDashboardFirmKnown,
    parameterizedPath: "/developer",
    moduleName: "Developer",
  }

  firmDeveloperApp: RouteConfig = {
    parentRoute: this.firmDeveloperRoot,
    parameterizedPath: "/:id",
    moduleName: "Manage App",
    detailsViewAction: DetailsViewAction.Push,
  }

  firmDeveloperAppConfiguration: RouteConfig = {
    parentRoute: this.firmDeveloperApp,
    parameterizedPath: "/configuration",
    detailsViewAction: DetailsViewAction.Push,
  }

  firmDeveloperAppKeys: RouteConfig = {
    parentRoute: this.firmDeveloperApp,
    parameterizedPath: "/keys",
    detailsViewAction: DetailsViewAction.Push,
  }

  orgDeveloperRoot: RouteConfig = {
    parentRoute: this.orgRoot,
    parameterizedPath: "/developer",
    moduleName: "Developer",
  }

  developerSettings: RouteConfig = {
    parentRoute: this.orgRoot,
    parameterizedPath: "/settings",
    moduleName: "Settings",
    detailsViewAction: DetailsViewAction.Push,
  }

  developerSettingsTeamMembers: RouteConfig = {
    parentRoute: this.developerSettings,
    parameterizedPath: "/team-members",
    moduleName: "Team Members",
    detailsViewAction: DetailsViewAction.Push,
  }

  developerSettingsAddTeamMember: RouteConfig = {
    parentRoute: this.developerSettingsTeamMembers,
    parameterizedPath: "/add",
  }

  orgDeveloperApp: RouteConfig = {
    parentRoute: this.orgDeveloperRoot,
    parameterizedPath: "/:id",
    moduleName: "Manage App",
    detailsViewAction: DetailsViewAction.Push,
  }

  orgDeveloperAppConfiguration: RouteConfig = {
    parentRoute: this.orgDeveloperApp,
    parameterizedPath: "/configuration",
    detailsViewAction: DetailsViewAction.Push,
  }

  orgDeveloperAppKeys: RouteConfig = {
    parentRoute: this.orgDeveloperApp,
    parameterizedPath: "/keys",
    detailsViewAction: DetailsViewAction.Push,
  }

  developerAppInstall: RouteConfig = {
    parameterizedPath: "/developer/install",
    moduleName: "App Install",
  }
}

export default createRoutes(FrontendStaticRoutes)

// Helper function that produces SharedObjectInfo for a report route
function reportSharedObjectInfo(routeParams: Record<string, string>): SharedObjectInfo {
  return {
    kind: ObjectKind.ReportPackage,
    id: routeParams.packageId || "",
    legalEntitySlug: routeParams.leSlug || "",
  }
}

// Helper function that produces SharedObjectInfo for a transaction route
function transactionSharedObjectInfo(routeParams: Record<string, string>): SharedObjectInfo {
  return {
    kind: ObjectKind.Transaction,
    id: routeParams.transactionId || "",
    legalEntitySlug: routeParams.leSlug || "",
  }
}

// Helper function that produces SharedObjectInfo for a category route
function categorySharedObjectInfo(routeParams: Record<string, string>): SharedObjectInfo {
  return {
    kind: ObjectKind.Category,
    id: routeParams.categoryId || "",
    legalEntitySlug: routeParams.leSlug || "",
  }
}

// Helper function that produces SharedObjectInfo for a department route
function departmentSharedObjectInfo(routeParams: Record<string, string>): SharedObjectInfo {
  return {
    kind: ObjectKind.Department,
    id: routeParams.departmentId || "",
    legalEntitySlug: routeParams.leSlug || "",
  }
}

// Helper function that produces SharedObjectInfo for a department route
function locationSharedObjectInfo(routeParams: Record<string, string>): SharedObjectInfo {
  return {
    kind: ObjectKind.Location,
    id: routeParams.locationId || "",
    legalEntitySlug: routeParams.leSlug || "",
  }
}

// Helper function that produces SharedObjectInfo for a party route
function partySharedObjectInfo(routeParams: Record<string, string>): SharedObjectInfo {
  return {
    kind: ObjectKind.Party,
    id: routeParams.partyId || "",
    legalEntitySlug: routeParams.leSlug || "",
  }
}
